import type { ReactNode } from 'react'
import './underway.css'

/**
 * Something is happening to this page right now, and here is the way out of it.
 *
 * **Not a Modal and not a Sheet.** A Modal is for a decision and covers the
 * page, which is the wrong thing to do to somebody reading mid-sentence; a
 * Sheet is for a record. What is wanted is neither: the page is still the thing
 * being used, and it is MOVING — so this states what is going on and offers the
 * one control that stops it, without taking the page or the focus.
 *
 * **It floats rather than banding**, which is `stale-build`'s reasoning and the
 * same constraint: everything sticky in the app offsets from `--topbar-h`, so a
 * band that added height would move the page under the reader — and here the
 * page is being scrolled for them, which is precisely when a shifting origin is
 * worst.
 *
 * **It never steals focus.** `role="status"` announces it where it stands, so
 * somebody typing in a comment keeps their caret. That is also what keeps it
 * clear of WCAG's timing criterion: a `for` seconds only decides how long the
 * strip is SHOWN, and the way out it names never expires — scrolling always
 * leaves, and the control that started it always ends it. Nothing is lost by
 * missing the window, which is the difference between a fading affordance and a
 * time limit on an interaction.
 */
export function Underway({
  children,
  action,
}: {
  /** What is going on, in a sentence somebody reads at a glance. */
  children: ReactNode
  /** The one way out. There is exactly one, or it is not this component. */
  action: ReactNode
}) {
  return (
    <div className="underway" role="status">
      <span className="underway__word">{children}</span>
      {action}
    </div>
  )
}
